import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleReports: React.FC = () => {
  const BASE = '/images/help/reports';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/rep-step-1.png`, alt: 'Daily email report example', caption: 'Step 1 – Your daily morning email report from Coderom' },
    ],
    step2: [
      { src: `${BASE}/rep-step-2.png`, alt: 'Report sections breakdown', caption: 'Step 2 – What each section of the daily report shows' },
    ],
    step3: [
      { src: `${BASE}/rep-step-3.png`, alt: 'Report settings configuration', caption: 'Step 3 – Customise report settings and delivery preferences' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Daily Reports & Notifications | CodeRom Help"
        description="Get daily profit reports, stock alerts & Telegram notifications from CodeRom."
      />
      <HelpBreadcrumbSchema slug="reports" name="Daily reports & notifications" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Daily reports & notifications</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Daily reports & notifications</h1>
                <p className="article-subtitle">
                  Stay on top of your business with daily email reports and real-time notifications. Know exactly what happened with your listings, orders, and prices every day.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 5 min read</span>
                  <span className="category">Reports</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Daily email report</a></li>
                  <li><a href="#step2">2. What's in the report</a></li>
                  <li><a href="#step3">3. Configure report settings</a></li>
                  <li><a href="#step4">4. Dashboard notifications</a></li>
                  <li><a href="#step5">5. Telegram notifications</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Daily email report</h2>
                  <p>
                    Every morning, Coderom sends you a summary email with everything that happened in the last 24 hours. The report is sent at 09:00 your local time for each connected marketplace.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Daily email report" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Daily reports are enabled by default. Check your spam folder if you're not receiving them — the emails come from notifications@coderom.com.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: What's in the report</h2>
                  <p>
                    Each daily report includes:
                  </p>

                  <div className="report-sections-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Section</th>
                          <th>What it shows</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Orders summary</strong></td>
                          <td>New orders, total revenue, total profit</td>
                        </tr>
                        <tr>
                          <td><strong>Price changes</strong></td>
                          <td>Products with price updates (up and down)</td>
                        </tr>
                        <tr>
                          <td><strong>Stock changes</strong></td>
                          <td>Products that went out of stock or came back</td>
                        </tr>
                        <tr>
                          <td><strong>Listing errors</strong></td>
                          <td>Any eBay listing errors or warnings</td>
                        </tr>
                        <tr>
                          <td><strong>AO summary</strong></td>
                          <td>Automatic ordering activity (if enabled)</td>
                        </tr>
                        <tr>
                          <td><strong>Account health</strong></td>
                          <td>eBay seller performance metrics</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: What's in the report" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Configure report settings</h2>
                  <p>
                    Go to <strong>Settings → Reports</strong> to customise:
                  </p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Daily email report</strong></td>
                          <td>Enable/disable the morning report</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Report email</strong></td>
                          <td>Which email address receives the report</td>
                          <td>Your primary email</td>
                        </tr>
                        <tr>
                          <td><strong>Report time</strong></td>
                          <td>When the report is sent</td>
                          <td>09:00 (default)</td>
                        </tr>
                        <tr>
                          <td><strong>Include zero-activity</strong></td>
                          <td>Send report even when nothing happened</td>
                          <td>Off</td>
                        </tr>
                        <tr>
                          <td><strong>Per-marketplace reports</strong></td>
                          <td>Separate report for each eBay store</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Configure report settings" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Dashboard notifications</h2>
                  <p>
                    In addition to email reports, the Coderom dashboard shows real-time notifications:
                  </p>
                  <ul>
                    <li>New orders received</li>
                    <li>Price changes detected</li>
                    <li>Stock alerts (out of stock warnings)</li>
                    <li>Listing errors</li>
                    <li>AO activity</li>
                  </ul>
                  <p>
                    Look for the notification bell icon in the top-right corner of the dashboard.
                  </p>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Telegram notifications</h2>
                  <p>
                    For instant alerts, connect Telegram:
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → Notifications</strong></li>
                    <li>Click <strong>Connect Telegram</strong></li>
                    <li>Follow the link to the Coderom Telegram bot</li>
                    <li>Send /start to the bot</li>
                    <li>Your Telegram is now linked — you'll receive instant alerts</li>
                  </ol>

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Telegram notifications are instant — much faster than email. Use them for critical alerts like new orders and stock-outs.
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your reports are set up, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/account-billing" className="next-step-card">
                      <div className="next-step-icon">💳</div>
                      <div className="next-step-content">
                        <h4>Account, subscription & billing</h4>
                        <p>Manage your subscription, payment methods, and invoices</p>
                      </div>
                    </Link>
                    <Link to="/help/getting-started" className="next-step-card">
                      <div className="next-step-icon">🚀</div>
                      <div className="next-step-content">
                        <h4>Getting started with Coderom</h4>
                        <p>Complete setup guide from account creation to first sale</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Manage your eBay orders from one place</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .report-sections-table,
        .settings-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .report-sections-table table,
        .settings-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .report-sections-table th,
        .settings-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .report-sections-table td,
        .settings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .report-sections-table tbody tr:last-child td,
        .settings-table tbody tr:last-child td {
          border-bottom: none;
        }
        .report-sections-table tbody tr:hover,
        .settings-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .report-sections-table,
          .settings-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleReports;
